import React from "react";
import { EmailTemplate } from "@common/components/email-template";
import { IPatientInviteFlow, PatientInviteFlowProps, PatientInviteStep, PatientInviteStepConfig } from "./types";
import { PatientDetails } from "../components/patient-details";
import { AdditionalPatientDetails } from "../components/additional-patient-details";
import { useTemplateController } from "./template";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { I18n } from "react-redux-i18n";
import { isEmail } from "@common/utils/email";
import { InvitePedigreeCreateDto } from "@common/dto/invite/pedigree/create-dto";
import { HttpErrorDto } from "@common/dto/http";
import { InviteCreatePedigreeResponse, InviteGetAdditionalDatasheetFormatDataParams } from "@common/actions/invites";
import { InviteDatasheetFormatGetDto } from "@common/dto/invite/datasheet/format-get-dto";
import { InviteDatasheetAdditionalDataUpdateDto } from "@common/dto/invite/datasheet/additional-data-update-dto";
import { QuestionnaireType } from "@common/dto/questionnaire/questionnaire-type";

export type NewPatientFlowBasicProps = {
  saveAdditionalDatasheetFormatData: (data: InviteDatasheetAdditionalDataUpdateDto) => Promise<void>;
  getAdditionalDatasheetFormatData: (params: InviteGetAdditionalDatasheetFormatDataParams) => Promise<InviteDatasheetFormatGetDto>;
  createPedigree: (data: InvitePedigreeCreateDto) => Promise<InviteCreatePedigreeResponse>;
  getBasicFolders: () => Promise<Array<FolderPrimaryListDto>>;
};

export type NewPatientFlowProps = PatientInviteFlowProps & NewPatientFlowBasicProps

export const useNewPatientFlow = (props: NewPatientFlowProps): IPatientInviteFlow => {
  const {
    getBasicFolders,
    onSubmit,
    setCurrentStep,
    questionnaire,
    showErrorMessage,
    createPedigree,
    getAdditionalDatasheetFormatData,
    saveAdditionalDatasheetFormatData,
  } = props;
  const {
    emailTemplate,
    emailSubject,
    setEmailSubject,
    setEmailTemplate
  } = useTemplateController();
  const [createdPedigreeResponse, setCreatedPedigreeResponse] = React.useState<InviteCreatePedigreeResponse | undefined>(undefined);
  const [submitting, setSubmitting] = React.useState(false);
  const [patientDetails, setPatientDetails] = React.useState<Partial<InvitePedigreeCreateDto> | undefined>(undefined);
  const [additionalDatasheetFormatData, setAdditionalDatasheetFormatData] = React.useState<Array<InviteDatasheetAdditionalDataUpdateDto>>([]);

  React.useEffect(() => {
    setEmailSubject(questionnaire?.emailTemplate.subject || '');
    setEmailTemplate(questionnaire?.emailTemplate.body || '');
  }, [questionnaire]);

  const stepsConfig: Partial<Record<PatientInviteStep, PatientInviteStepConfig>> = {
    [PatientInviteStep.patientDetails]: {
      component: (
        <PatientDetails
          showErrorMessage={showErrorMessage}
          getFolders={getBasicFolders}
          data={patientDetails}
          onChange={setPatientDetails}
        />
      ),
      previousStep: PatientInviteStep.receiverChooser,
      submitting,
      onSubmit: async () => {
        const validations = [
          {
            isValid: () => (
              patientDetails?.email
              && patientDetails.targetFolderId
              && patientDetails.pedigreeName
            ),
            msg: I18n.t('Please fill all fields')
          },
          {
            isValid: () => isEmail(patientDetails?.email),
            msg: I18n.t('Please provide a valid email')
          }
        ];

        const invalidValidation = validations.find(v => !v.isValid());

        if (invalidValidation) {
          showErrorMessage(invalidValidation.msg);

          return;
        }

        if (!patientDetails || !questionnaire) {
          return;
        }

        try {
          setSubmitting(true);
          const createPedigreeDto: InvitePedigreeCreateDto = {
            email: patientDetails.email!,
            targetFolderId: patientDetails.targetFolderId!,
            pedigreeName: patientDetails.pedigreeName!,
            fhq4: questionnaire.fhq4,
            individualQuestionnaire: questionnaire.questionnaireType === QuestionnaireType.individual,
            questionnaireId: questionnaire.questionnaireId,
            gender: patientDetails.gender!,
            wdcFolderId: questionnaire.wdcFolderId
          };

          const res = await createPedigree(createPedigreeDto);
          setCreatedPedigreeResponse(res);
          setCurrentStep(PatientInviteStep.patientAdditionalDetails);
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to create pedigree'));
        } finally {
          setSubmitting(false);
        }
      }
    },
    [PatientInviteStep.patientAdditionalDetails]: {
      component: questionnaire && createdPedigreeResponse && (
        <AdditionalPatientDetails
          showErrorMessage={showErrorMessage}
          onChange={setAdditionalDatasheetFormatData}
          questionnaire={questionnaire}
          globalId={createdPedigreeResponse.globalId}
          getAdditionalData={getAdditionalDatasheetFormatData}
        />
      ),
      submitting,
      previousStep: PatientInviteStep.patientDetails,
      onSubmit: async () => {
        if (additionalDatasheetFormatData) {
          try {
            setSubmitting(true);
            await Promise.all(additionalDatasheetFormatData.map(saveAdditionalDatasheetFormatData));
            setCurrentStep(PatientInviteStep.emailTemplate);
          } catch (err) {
            console.error(err);
            showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to update details'));
          } finally {
            setSubmitting(false);
          }

          return;
        }

        setCurrentStep(PatientInviteStep.emailTemplate);
      }
    },
    [PatientInviteStep.emailTemplate]: {
      component: (
        <EmailTemplate
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      submitBtnProps: {
        text: I18n.t('Send an Invite')
      },
      submitting,
      previousStep: PatientInviteStep.patientAdditionalDetails,
      onSubmit: async () => {
        if (!createdPedigreeResponse || !patientDetails || !questionnaire) {
          return;
        }

        try {
          setSubmitting(true);
          await onSubmit({
            globalIds: [createdPedigreeResponse.globalId],
            fhq4: questionnaire.fhq4,
            questionnaireId: questionnaire.questionnaireId,
            emailSubject,
            emailBody: emailTemplate,
          });
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to send an invite'));
        } finally {
          setSubmitting(false);
        }
      }
    },
  };

  return {
    stepsConfig,
    getFirstStep: () => PatientInviteStep.patientDetails,
  };
};